import { DashboardLead } from './components/leads-card.component';

/**
 * The shared reading of dashboard data.
 *
 * Both the dashboard's widgets and the Overview page ask the analytics API the
 * same questions, so the date maths and the lead shaping live here rather than
 * once in each. Two copies would drift, and the two screens would quietly
 * disagree about what "this month" means.
 */
export type Period = 'today' | 'month' | 'year';

/** Local calendar date, not UTC: a vendor's "today" is their own. */
const formatDateLocal = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const getDateRangeForPeriod = (period: Period) => {
  const now = new Date();
  let startDate: Date;
  let endDate: Date = now;

  switch (period) {
    case 'today':
      startDate = new Date(now);
      startDate.setHours(startDate.getHours() - 24);
      break;
    case 'month':
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 30);
      endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      break;
    case 'year':
      startDate = new Date(now);
      startDate.setFullYear(startDate.getFullYear() - 1);
      break;
  }

  return { startDate: formatDateLocal(startDate), endDate: formatDateLocal(endDate) };
};

export const getPreviousPeriodDateRange = (period: Period) => {
  const now = new Date();
  let startDate: Date;
  let endDate: Date;

  switch (period) {
    case 'today':
      endDate = new Date(now);
      endDate.setHours(endDate.getHours() - 24);
      startDate = new Date(endDate);
      startDate.setHours(startDate.getHours() - 24);
      break;
    case 'month':
      endDate = new Date(now);
      endDate.setDate(endDate.getDate() - 31);
      startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - 30);
      break;
    case 'year':
      endDate = new Date(now);
      endDate.setFullYear(endDate.getFullYear() - 1);
      startDate = new Date(endDate);
      startDate.setFullYear(startDate.getFullYear() - 1);
      break;
  }

  return { startDate: formatDateLocal(startDate!), endDate: formatDateLocal(endDate!) };
};

export const processLeadsData = (data: any): DashboardLead[] => {
  if (!Array.isArray(data)) return [];

  const emailGroups: any = {};

  data.forEach((event: any) => {
    if (event.eventType === 'ProductResultDownloadedEvent' && event.data?.userEmail) {
      const email = event.data.userEmail;
      const dateTime = event.dateTime || event.createdAt?.$date;

      if (!emailGroups[email]) {
        emailGroups[email] = { products: [], interactions: [], latestInteraction: null };
      }

      const enrichedProducts = [
        ...(event.data.enrichedProducts?.floor || []),
        ...(event.data.enrichedProducts?.walls || []),
        ...(event.data.enrichedProducts?.ceiling || []),
      ];

      const interaction = {
        date: dateTime,
        action: 'Downloaded design',
        products: enrichedProducts.map((product: any) => ({
          productId: product.id,
          surfaceType: product.surfaceType,
          productDetail: product,
        })),
      };

      enrichedProducts.forEach((product: any) => {
        const exists = emailGroups[email].products.some(
          (p: any) => p.productId === product.id && p.surfaceType === product.surfaceType,
        );
        if (!exists) {
          emailGroups[email].products.push({
            productId: product.id,
            surfaceType: product.surfaceType,
            productDetail: product,
          });
        }
      });

      emailGroups[email].interactions.push(interaction);

      if (!emailGroups[email].latestInteraction || new Date(dateTime) > new Date(emailGroups[email].latestInteraction)) {
        emailGroups[email].latestInteraction = dateTime;
      }
    }
  });

  return Object.entries(emailGroups)
    .map(([email, data]: [string, any], index) => {
      let engagement: 'hot' | 'warm' | 'cold' = 'cold';
      if (data?.interactions.length >= 5) engagement = 'hot';
      else if (data?.interactions.length >= 2) engagement = 'warm';

      return {
        id: index + 1,
        email,
        products: data?.products || [],
        lastInteraction: data?.latestInteraction,
        engagement,
        interactions: data?.interactions || [],
      };
    })
    .sort((a, b) => {
      const dateA = a.lastInteraction ? new Date(a.lastInteraction).getTime() : 0;
      const dateB = b.lastInteraction ? new Date(b.lastInteraction).getTime() : 0;
      return dateB - dateA;
    });
};
